import type { sideType } from "@/shared/stores";
import type { RefObject } from "react";

export interface PositionPopoverOptions {
  isOpen: boolean;
  contentRef: RefObject<HTMLDivElement | null>;
  anchorRect: DOMRect | null | undefined;
  side?: sideType;
  mounted: boolean;
  determinePopoverSide: (el: HTMLDivElement, wrapperHeight: number) => sideType;
}

export function usePopoverPosition({
  isOpen,
  contentRef,
  anchorRect,
  side,
  mounted,
  determinePopoverSide,
}: PositionPopoverOptions): void {
  if (isOpen && contentRef.current && anchorRect && mounted) {
    const content = contentRef.current;
    const gap = 8;
    const wrapper = content.parentElement as HTMLElement | null;
    if (!wrapper) return;

    // Get dimensions
    const wrapperRect = wrapper.getBoundingClientRect();
    const wrapperWidth = wrapperRect.width;
    const wrapperHeight = wrapperRect.height;

    // Reset styles
    wrapper.style.position = "fixed";
    wrapper.style.zIndex = "999999";
    wrapper.style.transform = "none";
    wrapper.style.transition = "none";

    // Determine side automatically if not provided
    const effectiveSide = side || determinePopoverSide(content, wrapperHeight);

    // Calculate center position
    const centerX = anchorRect.left + anchorRect.width / 2 - wrapperWidth / 2;
    const centerY = anchorRect.top + anchorRect.height / 2 - wrapperHeight / 2;

    // Apply positioning based on effectiveSide
    switch (effectiveSide) {
      case "bottom":
        wrapper.style.top = `${anchorRect.bottom + gap}px`;
        wrapper.style.left = `${centerX}px`;
        wrapper.style.bottom = "auto";
        wrapper.style.right = "auto";
        break;
      case "top":
        wrapper.style.bottom = `${window.innerHeight - anchorRect.top + gap}px`;
        wrapper.style.left = `${centerX}px`;
        wrapper.style.top = "auto";
        wrapper.style.right = "auto";
        break;
      case "left":
        wrapper.style.top = `${centerY}px`;
        wrapper.style.right = `${window.innerWidth - anchorRect.left + gap}px`;
        wrapper.style.bottom = "auto";
        wrapper.style.left = "auto";
        break;
      case "right":
        wrapper.style.top = `${centerY}px`;
        wrapper.style.left = `${anchorRect.right + gap}px`;
        wrapper.style.bottom = "auto";
        wrapper.style.right = "auto";
        break;
    }

    // Add viewport collision prevention
    const viewportPadding = 8;
    const rect = wrapper.getBoundingClientRect();

    // Prevent going off-screen horizontally
    if (rect.left < viewportPadding) {
      wrapper.style.left = `${viewportPadding}px`;
    } else if (rect.right > window.innerWidth - viewportPadding) {
      wrapper.style.left = `${window.innerWidth - wrapperWidth - viewportPadding}px`;
    }

    // Prevent going off-screen vertically
    if (rect.top < viewportPadding) {
      wrapper.style.top = `${viewportPadding}px`;
    } else if (rect.bottom > window.innerHeight - viewportPadding) {
      wrapper.style.top = `${window.innerHeight - wrapperHeight - viewportPadding}px`;
    }

    // Force a reflow
    wrapper.getBoundingClientRect();
  }
}
